/**
 * enemyCardModel — the PURE data→card projection for the enemy threat dossier
 * (extracted from `EnemyReveal` so the same mapping has ONE home).
 *
 * `EnemyReveal` (the in-run reveal queue) and the S159 Enemy Design Lab both
 * build a threat's `TradingCard` content from the SAME canonical fields, so this
 * module owns the mapping: the `EnemyRevealData` schema, the rarity ladder
 * (`deriveEnemyRarity`), the role/class/ability/flavour derivations, and the
 * one assembled `buildEnemyCardContent` projection. It is render/DOM-cold (no
 * `document`, no event bus, no timers) — a behaviour-preserving lift of the
 * logic that lived inline in `EnemyReveal`, so the in-run card is byte-identical
 * and the lab renders the REAL card (no transcribed copy that could drift).
 */
import type { TradingCardContent, TradingCardRarity } from "./TradingCard.ts";
import type { EnemyHoloOptions } from "./EnemyHolo.ts";
import type { EnemyConfig } from "../entities/types.ts";
import { localizedName, t } from "../utils/i18n.ts";

/** The data the card needs — a render-only projection of the enemy *type*. */
export interface EnemyRevealData {
  /** Codename (the display name, e.g. "Data Packet"). */
  name: string;
  /** Class code (e.g. "PKT"); falls back to the name's initials. */
  shortName?: string | undefined;
  hp: number;
  speed: number;
  reward: number;
  /** The `EnemyShapes` silhouette family (dart/block/cluster/…). */
  shape: string;
  /** The enemy's central-palette colour (already resolved). */
  colour: string;
  /** Drives the ROLE tag + the ability block (spawner/apex/…). */
  special?: { type: string; spawnCount?: number } | null | undefined;
  livesDamage?: number | undefined;
  firewallDamage?: number | undefined;
  /** Matchups — mapped to the card's weakness/resistance/immunity row. */
  weakness?: string | null | undefined;
  resistance?: string | null | undefined;
  immunities?: string[] | undefined;
  /** Stealth types are never pre-revealed (the cloak is the point). */
  stealth?: boolean | undefined;
}

/**
 * Task-012 (AC-16) — the static-config counterpart of `EnemyReveal`'s private
 * `toRevealData` (which projects a *live* `Enemy` instance). The Enemy Library
 * overlay has no live enemy to read (it lists the roster from `enemies.json`
 * directly), so it needs the SAME field mapping from the canonical
 * `EnemyConfig` alone — i18n seam included (`enemy.<id>` / `.short`, EN value as
 * the `t()` fallback, exactly the reveal card's convention) so the library
 * agrees with the in-run card byte-for-byte for every discovered type.
 */
export function configToRevealData(
  id: string,
  cfg: EnemyConfig,
): EnemyRevealData {
  return {
    name: localizedName("enemy", id, cfg.name),
    shortName: cfg.shortName
      ? localizedName("enemy", `${id}.short`, cfg.shortName)
      : undefined,
    hp: cfg.hp,
    speed: Math.round(cfg.speed),
    reward: cfg.reward,
    shape: cfg.shape ?? "diamond",
    colour: cfg.color,
    special: cfg.special ?? null,
    livesDamage: cfg.livesDamage,
    firewallDamage: cfg.firewallDamage,
    weakness: cfg.weakness ?? null,
    resistance: cfg.resistance ?? null,
    immunities: cfg.immunities,
    stealth: cfg.stealth,
  };
}

/**
 * Cold antagonist intros (FENRIR's voice) — render-only flavour, one per card.
 * Task-002 (2026-threat-archive-console) — these used to be raw EN literals
 * that bypassed `t()` (a DE device saw "Analyse it while you can." verbatim).
 * Each entry is now an i18n slug + the EN fallback (the BL-161 convention:
 * `t("enemy.taunt.<slug>", …, EN)`, EN mirrors the canonical table verbatim so
 * the EN card stays byte-identical); the DE values live in `translations.ts`.
 */
export const ENEMY_TAUNTS: readonly { slug: string; en: string }[] = [
  { slug: "new_toy", en: "Meet my next toy." },
  { slug: "unseen", en: "You haven't seen this one." },
  { slug: "something_new", en: "Something new for you." },
  { slug: "analyse_now", en: "Analyse it while you can." },
  { slug: "try_stop", en: "Try to stop this." },
  { slug: "fresh_nightmare", en: "A fresh nightmare." },
];

/** The localized taunt pool — resolved fresh on every call (never cached), so
 *  a runtime locale switch is picked up on the very next reveal/dossier. */
export function revealTaunts(): readonly string[] {
  return ENEMY_TAUNTS.map((tn) => t(`enemy.taunt.${tn.slug}`, undefined, tn.en));
}

/** Tier rank — drives the queue priority + the hold length (mirrors the foil ramp). */
export const TIER_RANK: Record<TradingCardRarity, number> = {
  basic: 0,
  rare: 1,
  epic: 2,
  secret: 2,
  legendary: 3,
};

export const CLASS_BADGE: Record<TradingCardRarity, string> = {
  basic: "CLASS-I",
  rare: "CLASS-II",
  epic: "CLASS-III",
  legendary: "CLASS-Ω",
  secret: "CLASS-X",
};

/** A role's i18n slug + the EN fallback text — `deriveRole` resolves the active
 *  locale at render via `t("enemy.role.<slug>", …, <EN>)`, so the EN dossier is
 *  byte-identical (the EN value moved verbatim into the table) and DE lights up. */
function roleEntry(d: EnemyRevealData): { slug: string; en: string } {
  if (d.special?.type === "finalBoss")
    return { slug: "overmind", en: "FINAL CORE · OVERMIND" };
  if ((d.livesDamage ?? 1) >= 3 || d.hp >= 1000)
    return { slug: "apex", en: "APEX THREAT" };
  if (d.special?.type === "spawnAdds") return { slug: "spawner", en: "SPAWNER" };
  if (d.special?.type === "shiftResistance")
    return { slug: "shifter", en: "SHIFTER" };
  // The Threat-Pack specials read by their FUNCTION, not by a generic HP/speed
  // fallback (jammer/overseer would otherwise mis-read as "HEAVY"). These are the
  // player-facing dossier labels — deliberately distinct from (but aligned with)
  // EnemyDirector.classifyRole's internal apex/anchor/flanker/support/pressure.
  if (d.special?.type === "jammer")
    return { slug: "suppressor", en: "SUPPRESSOR" };
  if (d.special?.type === "overseer")
    return { slug: "commander", en: "COMMANDER" };
  if (d.special?.type === "leech") return { slug: "siphon", en: "SIPHON" };
  if (d.special?.type === "regenerator") return { slug: "mender", en: "MENDER" };
  if (d.special?.type === "splitter") return { slug: "divider", en: "DIVIDER" };
  if ((d.firewallDamage ?? 0) > 0) return { slug: "breacher", en: "BREACHER" };
  if (d.hp >= 250) return { slug: "heavy", en: "HEAVY" };
  if (d.speed >= 80) return { slug: "rusher", en: "RUSHER" };
  return { slug: "intruder", en: "INTRUDER" };
}

/** A terse ROLE descriptor from the type's stats/special (the dossier sub-tag),
 *  localized at render through the i18n seam (EN fallback = the canonical text). */
export function deriveRole(d: EnemyRevealData): string {
  const { slug, en } = roleEntry(d);
  return t(`enemy.role.${slug}`, undefined, en);
}

/** Class code — the shortName, or the name's word-initials as a fallback. */
export function classCode(d: EnemyRevealData): string {
  if (d.shortName) return d.shortName;
  return d.name
    .split(/[\s-]+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

/**
 * The threat tier → the card rarity (guardrail ③). trash = BASIC · elite =
 * RARE/EPIC · boss = EPIC · final_boss = LEGENDARY · stealth = SECRET.
 * Exported so the rarity→tier mapping is unit-testable without the DOM.
 */
export function deriveEnemyRarity(d: EnemyRevealData): TradingCardRarity {
  if (d.stealth) return "secret";
  if (d.special?.type === "finalBoss") return "legendary";
  const lives = d.livesDamage ?? 1;
  if (lives >= 3 || d.hp >= 1000) return "epic"; // boss-class
  if (d.special?.type === "spawnAdds" && lives >= 2) return "epic"; // elite spawner
  if (lives >= 2 || d.hp >= 250 || d.special) return "rare"; // elite
  return "basic"; // trash
}

/** One ability branch: the i18n slug, the EN title + desc fallbacks (the
 *  canonical text moved verbatim), and the desc interpolation params. The
 *  symbol prefix (▾ ❖ ◎ …) lives in the title text and is shared across both
 *  locales; the dynamic counts (spawnCount / firewallDamage) stay data-driven
 *  (D-256/BL-158) and interpolate in EN and DE alike. */
function abilityEntry(d: EnemyRevealData): {
  slug: string;
  titleEN: string;
  descEN: string;
  params?: Record<string, string | number>;
} {
  const sp = d.special?.type;
  if (sp === "finalBoss")
    return {
      slug: "overmind",
      titleEN: "⟁ OVERMIND",
      descEN:
        "Splits into phases at 70% / 30% integrity; each phase spawns a fresh wave of adds. The mainframe's last stand.",
    };
  if (sp === "spawnAdds") {
    const n = d.special?.spawnCount;
    return {
      slug: "spawn_adds",
      titleEN: "⊕ SPAWN ADDS",
      descEN:
        "Breaches into {{count}}additional threats mid-run — finish it before it multiplies the swarm.",
      params: { count: n ? `${n} ` : "" },
    };
  }
  if (sp === "shiftResistance")
    return {
      slug: "polymorph",
      titleEN: "✷ POLYMORPH",
      descEN:
        "Cycles its resistance on an interval — no single element holds against it for long.",
    };
  if (sp === "jammer")
    return {
      slug: "suppression_field",
      titleEN: "▾ SUPPRESSION FIELD",
      descEN:
        "Jams nearby towers — slashes their fire rate while it's in range. Spread your towers or take it down first.",
    };
  if (sp === "overseer")
    return {
      slug: "aura_host",
      titleEN: "❖ AURA HOST",
      descEN:
        "Shields the pack around it — nearby threats take reduced damage. Kill the host and the aura collapses.",
    };
  if (sp === "leech")
    return {
      slug: "credit_siphon",
      titleEN: "◎ CREDIT SIPHON",
      descEN:
        "Drains your credits for as long as it lives — a hoarder's bane. Kill it fast to stop the bleed.",
    };
  if (sp === "regenerator")
    return {
      slug: "self_repair",
      titleEN: "✚ SELF-REPAIR",
      descEN:
        "Knits its integrity back every second — chip damage won't hold it. Break it with burst.",
    };
  if (sp === "splitter") {
    const n = d.special?.spawnCount;
    return {
      slug: "fracture",
      titleEN: "⊟ FRACTURE",
      descEN:
        "Killing it splits it into {{count}}fast splinters — bring AoE or it floods the lane.",
      params: { count: n ? `${n} ` : "" },
    };
  }
  if ((d.firewallDamage ?? 0) > 0)
    return {
      slug: "firewall_breach",
      titleEN: "▣ FIREWALL BREACH",
      descEN:
        "Tears {{dmg}} off the firewall on contact — a dedicated breacher, intercept it early.",
      params: { dmg: d.firewallDamage ?? 0 },
    };
  if (d.hp >= 250)
    return {
      slug: "hardened",
      titleEN: "◼ HARDENED",
      descEN:
        "Heavily armoured — soaks punishment. Concentrate fire to crack the shell.",
    };
  if (d.speed >= 80)
    return {
      slug: "rush",
      titleEN: "» RUSH",
      descEN: "Fast and light — it races the lane. Slow it or intercept early.",
    };
  return {
    slug: "intrusion",
    titleEN: "· INTRUSION",
    descEN: "A standard intrusion packet — the baseline threat on the wire.",
  };
}

/** The ability block — the `special` (or a role-derived line) as the "attack",
 *  localized at render via the i18n seam. The EN title/desc are the fallbacks,
 *  so the EN card is byte-identical; the count/damage interpolate in BOTH
 *  languages (kept data-driven — no hardcoded numbers). */
export function deriveAbility(d: EnemyRevealData): { title: string; desc: string } {
  const { slug, titleEN, descEN, params } = abilityEntry(d);
  return {
    title: t(`enemy.ability.${slug}.title`, undefined, titleEN),
    desc: t(`enemy.ability.${slug}.desc`, params, descEN),
  };
}

/** An element/null → the matchup-row display label. The element name itself
 *  is a central symbol that stays identical across locales (DAMAGE/KINETIC/…);
 *  only the empty-matchup placeholder is localized via the i18n seam. */
export function elementLabel(el: string | null | undefined): string {
  return el ? el.toUpperCase() : t("card.label.none", undefined, "— none —");
}

/** Pick a flavour taunt (render-only randomness — never a sim quantity). */
export function pickTaunt(arr: readonly string[] = revealTaunts()): string {
  return arr[Math.floor(Math.random() * arr.length)] ?? "";
}

/** The assembled enemy card projection — the rarity, the `TradingCard` content,
 *  and the `EnemyHolo` art options. The ONE place enemy data becomes a card. */
export function buildEnemyCardContent(d: EnemyRevealData): {
  rarity: TradingCardRarity;
  content: TradingCardContent;
  holo: EnemyHoloOptions;
} {
  const rarity = deriveEnemyRarity(d);
  const immune =
    d.immunities && d.immunities.length
      ? d.immunities.join(" · ").toUpperCase()
      : undefined;
  const content: TradingCardContent = {
    kind: "enemy",
    rarity,
    colour: d.colour,
    classification: t("card.label.classification", undefined, "⚠ THREAT MANIFEST"),
    classBadge: CLASS_BADGE[rarity],
    name: d.name,
    subtitle: `${classCode(d)} · ${deriveRole(d)}`,
    stats: [
      { k: t("card.label.integrity", undefined, "INTEGRITY"), v: `${d.hp} HP`, tone: "hot" },
      { k: t("card.label.velocity", undefined, "VELOCITY"), v: String(d.speed) },
      { k: t("card.label.bounty", undefined, "BOUNTY"), v: `${d.reward} ¢`, tone: "gold" },
    ],
    weakness: elementLabel(d.weakness),
    resistance: elementLabel(d.resistance),
    ...(immune ? { immunities: immune } : {}),
    ability: deriveAbility(d),
    footerLeft: `SENTINEL-7 · ${classCode(d)}`,
    flavour: pickTaunt(),
    pips: TIER_RANK[rarity] + 1,
  };
  return {
    rarity,
    content,
    holo: { shape: d.shape, colour: d.colour, tier: rarity },
  };
}
